import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Datagrid from "../../components/Datagrid/Datagrid";
import DatagridLoading from "../../components/DatagridLoading/DatagridLoading";
import ModalWrapper from "../../components/ModalWrapper/ModalWrapper";
import { useAuth } from "../../context/authContext";
import { getCustomSitesData, getUserAllocations } from "../../services/manageSitesService";
import { ROUTES } from "../../constants";
import { getProvisionedMachines } from "../../services/manageSiteMachinesService";

const normalizeRows = (payload) => {
	if (!payload) return [];
	if (Array.isArray(payload)) return payload;
	if (Array.isArray(payload?.data)) return payload.data;
	if (Array.isArray(payload?.data?.data)) return payload.data.data;
	return [];
};

const toBool = (value, fallback = false) => {
	if (typeof value === "boolean") return value;
	if (typeof value === "number") return value === 1;
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		if (["true", "1", "yes", "on", "enabled"].includes(normalized)) return true;
		if (["false", "0", "no", "off", "disabled"].includes(normalized)) return false;
	}
	return fallback;
};

const normalizeProvisionRow = (row = {}) => ({
	id: row.site_machine_id || row.id,
	site_machine_id: row.site_machine_id || row.id,
	machine_id: row.machine_id,
	machine_name: row.machine_name,
	machine_number: row.machine_number,
	machine_serial_number: row.machine_product_name || row.sm_machine_product_name,
	machine_status: row.machine_status || row.sm_machine_status,
	customer_number: row.customer_number || row.cus_number,
	customer_name: row.customer_name || row.cus_name,
	customer_site_number: row.customer_site_number || row.site_number,
	customer_site_name: row.customer_site_name || row.site_name,
	site_id: row.site_id,
	cus_id: row.cus_id,
	site_line_id: row.site_line_id,
	line_id: row.line_id,
	line_name: row.line_name,
	line_number: row.line_number,
});

const ManageLines = () => {
	const { t } = useTranslation();
	const { user } = useAuth();
	const navigate = useNavigate();
	const [lines, setLines] = useState([]);
	const [machines, setMachines] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	const [machineModalState, setMachineModalState] = useState({
		open: false,
		line: null,
		machines: [],
	});

	const actorRoleType = useMemo(
		() => String(user?.role?.role_type || user?.role?.role_name || "").trim().toLowerCase(),
		[user?.role?.role_type, user?.role?.role_name]
	);

	const actorSiteIds = useMemo(() => {
		if (!Array.isArray(user?.allocations)) return [];
		return user.allocations
			.map((allocation) => ({
				site_id: Number(allocation?.site_id),
				cus_id: Number(allocation?.cus_id),
			}))
			.filter(
				(allocation) =>
					Number.isFinite(allocation.site_id) &&
					allocation.site_id > 0 &&
					Number.isFinite(allocation.cus_id) &&
					allocation.cus_id > 0
			);
	}, [user?.allocations]);

	const effectiveActorSiteIds = useMemo(
		() => (actorSiteIds.length > 0 ? actorSiteIds : fallbackActorSiteIds),
		[actorSiteIds, fallbackActorSiteIds]
	);

	const actorHasAllSiteVisibility = useMemo(
		() =>
			Number(user?.user_id) === 1 ||
			((actorRoleType === "admin" || toBool(user?.role?.permissions?.site_management?.manage_all_sites)) &&
				effectiveActorSiteIds.length === 0),
		[user?.user_id, actorRoleType, user?.role?.permissions?.site_management?.manage_all_sites, effectiveActorSiteIds.length]
	);

	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (Number(user?.user_id) === 1 || actorSiteIds.length > 0 || !user?.user_id) {
				if (mounted) setFallbackActorSiteIds([]);
				return;
			}
			try {
				const resp = await getUserAllocations(user.user_id);
				const allocations = Array.isArray(resp?.data?.allocations) ? resp.data.allocations : [];
				const ids = allocations
					.map((allocation) => ({
						site_id: Number(allocation?.site_id),
						cus_id: Number(allocation?.cus_id),
					}))
					.filter(
						(allocation) =>
							Number.isFinite(allocation.site_id) &&
							allocation.site_id > 0 &&
							Number.isFinite(allocation.cus_id) &&
							allocation.cus_id > 0
					);
				if (mounted) setFallbackActorSiteIds(ids);
			} catch (_err) {
				if (mounted) setFallbackActorSiteIds([]);
			}
		};
		loadFallbackActorScope();
		return () => {
			mounted = false;
		};
	}, [actorSiteIds, user?.user_id]);

	useEffect(() => {
		let mounted = true;
		const loadLines = async () => {
			setLoading(true);
			setError("");
			try {
				const lineQuery = `
					SELECT
						sl.site_line_id,
						sl.line_id,
						sl.site_id,
						sl.cus_id,
						COALESCE(l.line_name, CONCAT('Line ', sl.line_id)) AS line_name,
						COALESCE(l.line_number::text, '') AS line_number,
						l.line_description,
						COALESCE(l.status, sl.status) AS line_status,
						s.customer_site_number,
						s.customer_site_name,
						c.customer_number,
						c.customer_name
					FROM site_lines sl
					JOIN lines l ON l.line_id = sl.line_id
					JOIN sites s ON s.site_id = sl.site_id
					LEFT JOIN customers c ON c.cus_id = sl.cus_id
					WHERE LOWER(COALESCE(sl.status, 'active')) = 'active'
					  AND LOWER(COALESCE(l.status, 'active')) = 'active'
					ORDER BY s.customer_site_name ASC, l.line_name ASC, sl.site_line_id ASC
				`;
				const [linesResp, machinesResp] = await Promise.all([
					getCustomSitesData(lineQuery),
					getProvisionedMachines({ includeInactive: true }),
				]);

				if (!mounted) return;
				setLines(normalizeRows(linesResp?.data ?? linesResp));
				setMachines((machinesResp?.status ? machinesResp.data || [] : []).map(normalizeProvisionRow));
			} catch (err) {
				if (mounted) setError(`Failed to fetch lines: ${err.message}`);
			} finally {
				if (mounted) setLoading(false);
			}
		};

		loadLines();
		return () => {
			mounted = false;
		};
	}, []);

	const scopedLines = useMemo(() => {
		if (actorHasAllSiteVisibility) return lines;
		return lines.filter((line) =>
			effectiveActorSiteIds.some(
				(allocation) =>
					Number(allocation.site_id) === Number(line.site_id) &&
					Number(allocation.cus_id) === Number(line.cus_id)
			)
		);
	}, [actorHasAllSiteVisibility, effectiveActorSiteIds, lines]);

	const scopedMachines = useMemo(() => {
		if (actorHasAllSiteVisibility) return machines;
		return machines.filter((machine) =>
			effectiveActorSiteIds.some(
				(allocation) =>
					Number(allocation.site_id) === Number(machine.site_id) &&
					Number(allocation.cus_id) === Number(machine.cus_id)
			)
		);
	}, [actorHasAllSiteVisibility, effectiveActorSiteIds, machines]);

	const linesWithMachineCounts = useMemo(() => {
		const countsByLine = scopedMachines.reduce((acc, machine) => {
			const key = Number(machine.site_line_id);
			if (!Number.isFinite(key)) return acc;
			acc[key] = (acc[key] || 0) + 1;
			return acc;
		}, {});
		return scopedLines.map((line) => ({
			...line,
			machine_count: countsByLine[Number(line.site_line_id)] || 0,
		}));
	}, [scopedLines, scopedMachines]);

	const canNavigateLine = (line) => Boolean(line?.site_line_id);

	const handleLineNameClick = (line) => {
		if (!canNavigateLine(line)) return;
		const query = new URLSearchParams({
			site_line_id: line.site_line_id,
			line_name: line.line_name || `Line ${line.line_id}`,
			line_number: line.line_number || "",
		}).toString();
		navigate(`${ROUTES.MACHINES}?${query}`);
	};

	const openMachineModal = (line) => {
		const assignedMachines = scopedMachines.filter(
			(machine) => Number(machine.site_line_id) === Number(line.site_line_id)
		);
		setMachineModalState({
			open: true,
			line,
			machines: assignedMachines,
		});
	};

	const closeMachineModal = () => {
		setMachineModalState({
			open: false,
			line: null,
			machines: [],
		});
	};

	if (loading) {
		return (
			<DatagridLoading
				message=""
				actionButtons={0}
				columns={6}
				rows={8}
			/>
		);
	}

	if (error) {
		return <Alert severity="error">{error}</Alert>;
	}

	const columns = [
		{
			field: "line_name",
			headerName: "Line Name",
			flex: 1.4,
			renderCell: (params) => {
				const isNavigable = canNavigateLine(params.row);
				return (
					<Box
						component="span"
						onClick={() => handleLineNameClick(params.row)}
						sx={{
							cursor: isNavigable ? "pointer" : "default",
							color: "primary.main",
							transition: "color 0.15s ease, text-decoration-color 0.15s ease",
							textDecoration: "underline",
							textDecorationColor: "transparent",
							"&:hover": isNavigable
								? {
									color: "primary.main",
									textDecorationColor: "currentColor",
								}
								: undefined,
						}}
					>
						{params.value || "-"}
					</Box>
				);
			},
		},
		{
			field: "line_description",
			headerName: "Description",
			flex: 1.6,
			renderCell: (params) => params.value || "-",
		},
		{ field: "customer_site_name", headerName: t("LABELS.SITE_NAME"), flex: 1.5 },
		{ field: "customer_site_number", headerName: t("TABLE_HEADERS.SITE_NUMBER"), flex: 1 },
		{ field: "customer_number", headerName: t("TABLE_HEADERS.CUSTOMER_NUMBER"), flex: 1 },
		{
			field: "machine_count",
			headerName: t("UI_TEXT.MACHINES"),
			width: 180,
			align: "center",
			headerAlign: "center",
			renderCell: (params) => (
				<Button
					variant="text"
					size="small"
					onClick={(event) => {
						event.stopPropagation();
						openMachineModal(params.row);
					}}
				>
					{params.value ?? 0}
				</Button>
			),
		},
	];

	const machineColumns = [
		{ field: "machine_name", headerName: t("TABLE_HEADERS.MACHINE_NAME"), flex: 1.3 },
		{
			field: "machine_number",
			headerName: t("TABLE_HEADERS.MACHINE_NUMBER"),
			flex: 1,
			renderCell: (params) => params.row.machine_number || "-",
		},
		{ field: "machine_serial_number", headerName: t("LABELS.MACHINE_SERIAL"), flex: 1.2 },
		{ field: "machine_status", headerName: t("TABLE_HEADERS.MACHINE_STATUS"), flex: 1 },
	];

	return (
		<Box>
			<Datagrid
				loading={loading}
				data={linesWithMachineCounts}
				columns={columns}
				checkboxSelection={false}
				rowIdField="site_line_id"
				sortBy={[{ field: "line_name", sort: "asc" }]}
				showSearchBar
				hideFilterFromColumnMenu
				lockActiveColumnInManageColumns
				preventHidingLastColumn
			/>
			<ModalWrapper
				open={machineModalState.open}
				setOpen={() => {}}
				onClose={closeMachineModal}
				subtitle={"Review details"}
				title={
					machineModalState.line
						? `${machineModalState.line.line_name || "Line"} - ${t("UI_TEXT.MACHINES")}`
						: t("UI_TEXT.MACHINES")
				}
				content={
					machineModalState.open ? (
						<Datagrid
							data={machineModalState.machines}
							columns={machineColumns}
							checkboxSelection={false}
							rowIdField="site_machine_id"
							showSearchBar
							hideFilterFromColumnMenu
							lockActiveColumnInManageColumns
							preventHidingLastColumn
						/>
					) : (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
							<CircularProgress />
						</Box>
					)
				}
				actions={<Button onClick={closeMachineModal}>{t("BUTTON.CLOSE")}</Button>}
			/>
		</Box>
	);
};

export default ManageLines;
